import { editUnpublishedQuilt, getQuiltsMega } from 'api/http-client/queries/quiltQueries';
import { QuiltsResponseMega } from 'api/http-client/types';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { settingsRowM, settingsRowS, settingsRowMobile } from '../helpers/carousel/get-settings-row';
import { OptionsWrapEnum, OptionsRowType } from '../common/enums/carousel/carousel.enum';
import { createSubArraysFromQuilts } from '../helpers/carousel/create-sub-arrays-from-quilts';
import { isVac } from 'utilities/is-vac';

interface UseCarouselHomeReturnModel {
  option: OptionsWrapEnum;
  toggleZIndex?: (control: boolean) => void;
  getMovePositionBySide?: () => string;
  handleTransitionEnd?: () => void;
  data: (QuiltsResponseMega | null)[][] | undefined;
  setData: Dispatch<SetStateAction<(QuiltsResponseMega | null)[][] | undefined>>;
  settingData: DataSettingModel;
  loading: boolean;
}

export interface DataSettingModel {
  limitMega: number;
  groupLimit: number;
  rowLimit: number;
}

export const useCarouselHome = (): UseCarouselHomeReturnModel => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<(QuiltsResponseMega | null)[][]>();
  const [loading, setLoading] = useState<boolean>(false);

  const matches2000 = useMediaQuery('(max-width: 2000px)');
  const matches1650 = useMediaQuery('(max-width: 1650px)');
  const matches1240 = useMediaQuery('(max-width: 1240px)');
  const matches1177 = useMediaQuery('(max-width: 1177px)');
  const matches600 = useMediaQuery('(max-width: 600px)');

  const [settingData, setSettingData] = useState<DataSettingModel>({
    limitMega: 150,
    groupLimit: 7,
    rowLimit: matches600 ? 9 : 5
  });

  let settings: { settingsRow: OptionsRowType[][]; option: OptionsWrapEnum } = useMemo(() => {
    if (matches600) { 
      return {
        settingsRow: settingsRowMobile,
        option: OptionsWrapEnum.mobile,
      };
    } else if (matches1650) {
      return {
        settingsRow: settingsRowS,
        option: OptionsWrapEnum.s,
      };
    } else if (matches2000) {
      return {
        settingsRow: settingsRowM,
        option: OptionsWrapEnum.m,
      };
    } else {
      return {
        settingsRow: settingsRowM,
        option: OptionsWrapEnum.m,
      };
    }
  }, [matches2000, matches1650, matches1240, matches1177, matches600]);

  useEffect(() => {
    // set group limit
    if (matches600) {
      setSettingData((data) => {
        return {
          ...data,
          groupLimit: 3,
          rowLimit: 9
        };
      });
    } else if (matches2000) {
      setSettingData((data) => {
        return {
          ...data,
          groupLimit: 4,
          rowLimit: 5
        };
      });
    }

    setLoading(true);

    getQuiltsMega(settingData.limitMega)
      .then((data) => {
        if (!data) return;

        // visible functionality (view quilt on home page)
        const visibleId = searchParams.get('visible');

        if (visibleId) {
          editUnpublishedQuilt(visibleId).then((quilt) => {
            const dataWithoutThisQuilt = data.filter((item) => item.id !== +visibleId);

            setData(getPreparingData([quilt, ...dataWithoutThisQuilt]));
          });
        } else {
          setData(getPreparingData(data));
        }
      })
      .finally(() => setLoading(false));
  }, [matches2000, matches1650, matches1240, matches1177, matches600]);

  const getPreparingData = (data: QuiltsResponseMega[]) => {
    if (data.length < settingData.groupLimit) {
      return [data];
    }

    const total = createSubArraysFromQuilts(settings, data);

    return total;
  };

  return {
    option: settings.option,
    data,
    loading,
    setData,
    settingData,
  };
};
